// React
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { Controller, useFieldArray, useForm } from 'react-hook-form';
// ライブラリ
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
// カスタムフック
import { useEditStateStorage } from '../../../hooks/useEditStateStorage';
// ユーティリティ
import { validationSchema } from '../../../utils/validationUtils';
import { successMessages, errorMessages } from '../../../utils/messagesUtils';
import { menu } from '../../../constants';
// スタイル
import sty from './Main.module.css';

const Certifications = ({ years, months }) => {
    const navigate = useNavigate();
    // props
    Certifications.propTypes = {
        years: PropTypes.array.isRequired,
        months: PropTypes.array.isRequired,
    };
    const [originalData, setOriginalData] = useState(null);

    const defaultQualifications = {
        qualifications: [
            {
                qualification_name: '',
                qualification_year: '',
                qualification_month: ''
            }
        ]
    };

    const { handleSubmit, control, formState: { errors }, reset, watch } = useForm({
        mode: 'onSubmit', // 保存時にバリデーションを実行
        reValidateMode: 'onSubmit', // 一度エラーが発生した後も、次の保存時にのみ再バリデーションを実行
        defaultValues: defaultQualifications,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'qualifications',
    });

    const watchAllFields = watch();
    const getFormData = useCallback(() => watchAllFields, [watchAllFields]);

    // カスタムフックの使用
    const { checkForEditData, clearStoredData } = useEditStateStorage(
        'certifications', // ストレージキープレフィックス
        getFormData,      // フォームデータ取得関数
        reset,            // フォームリセット関数
        originalData,     // オリジナルデータ
    );

    // フォーム送信時の処理
    const onSubmit = async (data) => {
        // 項目が全て空のデータを削除する
        const formattedData = {
            qualifications: data.qualifications.filter(item => {
                return Object.values(item).some(value => value !== "");
            })
        };
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certifications`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json", 
                },
                body: JSON.stringify(formattedData),
                credentials: "include",
            });
            const result = await response.json();
            if (response.status === 200 && result.status === 'OK') {
                clearStoredData();
                setOriginalData(formattedData);
                reset(formattedData);
                Swal.fire({ icon: 'success', title: successMessages.operation.created });
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else {
                    throw new Error();
                }
            }
        } catch (e) {
            await Swal.fire({ icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    };

    // サーバーからデータを取得
    const fetchData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certifications`, {
                method: "GET",
                headers: {
                    'Accept': "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });
            const result = await response.json();
            if(response.status === 200 && result.status === 'OK'){
                const data = result?.data
                if(data){
                    if (!data.qualifications || data.qualifications.length === 0) {
                        setOriginalData(defaultQualifications);
                        reset(defaultQualifications);
                        checkForEditData(defaultQualifications);
                    } else {
                        const formattedData = {
                            qualifications: data.qualifications.map(item => ({
                                qualification_name: item.qualification_name || '',
                                qualification_year: item.qualification_year || '',
                                qualification_month: item.qualification_month || '',
                            })),
                        };
                        setOriginalData(formattedData);
                        reset(formattedData);
                        checkForEditData(formattedData);
                    }
                }
            } else {
                if(response.status === 401){
                    navigate(menu.ENGINEER.SIGNIN.PATH)
                } else if(response.status === 404) {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_not_found;
                    throw error;
                } else {
                    const error = new Error();
                    error.front_message = errorMessages.common.data_fetch_failed;
                    throw error;
                }
            }
        } catch(e) {
            await Swal.fire({ icon: 'error',
                html: `${e.front_message || errorMessages.api.server_error}`,
                customClass: {
                    htmlContainer: 'large-text'
                }
            })
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => { 
        if (fields.length === 0) 
            append({ qualification_name: '', qualification_year: '', qualification_month: '' });  
    }, [fields, append]);

    // エンターキーでの送信を防ぐ
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className={sty['form']} data-testid="Certifications-form">
            <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-pc']} ${sty['form-tab__content-inner-pt']}`}>
                <table className={sty['form__qualification-pc']}>
                    <tbody>
                        <tr>
                            <th>資格名</th>
                            <th className={fields.length === 1 ? sty['table__last-tr-th'] : ''}>取得年</th>
                            <th className={fields.length === 1 ? sty['table__last-tr-th'] : ''}>取得月</th>
                            {fields.length > 1 && (
                                <th className={sty['table__blank-th']}></th>
                            )}
                        </tr>
                        {fields.map((field, index) => {
                            const qualification_name = `qualifications.${index}.qualification_name`
                            const qualification_year = `qualifications.${index}.qualification_year`
                            const qualification_month = `qualifications.${index}.qualification_month`
                            return (
                            <tr key={field.id}>
                                {/* 資格名、取得年、取得月の入力フィールド */}
                                <td>
                                    <Controller
                                        control={control}
                                        name={qualification_name}
                                        rules={validationSchema.qualification_name(watch(qualification_year), watch(qualification_month))}
                                        render={({ field }) => <input type="text" placeholder="基本情報技術者" {...field} />}
                                    />
                                    {errors.qualifications?.[index]?.qualification_name && <p className="form__validation-msg" data-testid="pc_qualification_name_error_message">{errors.qualifications[index].qualification_name.message}</p>}
                                </td>
                                <td className={sty['form__select-outer']}>
                                    <Controller
                                        control={control}
                                        name={qualification_year}
                                        rules={validationSchema.qualification_year(watch(qualification_name), watch(qualification_month))}
                                        render={({ field }) => (
                                            <select aria-label="取得年" {...field}>
                                                <option value="">選択</option>
                                                {years.map((y) => (
                                                    <option key={y} value={y}>{y + '年'}</option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                    {errors.qualifications?.[index]?.qualification_year && <p className="form__validation-msg" data-testid="pc_year_error_message">{errors.qualifications[index].qualification_year.message}</p>}
                                </td>
                                <td className={sty['form__select-outer']}>
                                    <Controller
                                        control={control}
                                        name={qualification_month}
                                        rules={validationSchema.qualification_month(watch(qualification_name), watch(qualification_year))}
                                        render={({ field }) => (
                                            <select aria-label="取得月" {...field}>
                                                <option value="">選択</option>
                                                {months.map((m) => (
                                                    <option key={m} value={m}>{m + '月'}</option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                    {errors.qualifications?.[index]?.qualification_month && <p className="form__validation-msg" data-testid="pc_month_error_message">{errors.qualifications[index].qualification_month.message}</p>}
                                </td>
                                <td>
                                    {fields.length > 1 && (
                                        <button type="button" className={sty['delete-each-btn']} onClick={() => remove(index)}>削除</button>
                                    )}
                                </td>
                            </tr>
                        )}
                    )}
                    </tbody>
                </table>
                <div className={sty['form__btn-left']}>
                    <button type="button" className={`${sty['plus']} ${sty['plus-pc']}`} onClick={() => append({ qualification_name: '', qualification_year: '', qualification_month: '' })} data-testid="pc_plus_button">+</button>
                </div>
            </div>

            {/* SP版 */}
            {fields.map((field, index) => {
                const qualification_name = `qualifications.${index}.qualification_name`
                const qualification_year = `qualifications.${index}.qualification_year`
                const qualification_month = `qualifications.${index}.qualification_month`
                return (
                <div className={`${sty['form-tab__content-inner']} ${sty['form-tab__content-inner-sp']}`} key={field.id}>
                    <div className={sty['form__qualification-sp']}>
                        <div className={`${sty['form__flex']} ${sty['form__flex-sp-mb']}`}>
                            <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                <div className={sty['form__label']}>資格名</div>
                            </div>
                            <div className={sty['form__input']}>
                                <Controller
                                    control={control}
                                    name={`qualifications.${index}.qualification_name`}
                                    rules={validationSchema.qualification_name(watch(qualification_year), watch(qualification_month))}
                                    render={({ field }) => (
                                        <input type="text" placeholder="基本情報技術者" {...field} />
                                    )}
                                />
                                {errors.qualifications?.[index]?.qualification_name && <p className="form__validation-msg" data-testid="pc_qualification_name_error_message">{errors.qualifications[index].qualification_name.message}</p>}
                            </div>
                        </div>

                        <div className={`${sty['form__flex']} ${sty['form__flex-sp-mb']}`}>
                            <div className={`${sty['form__label-flex']} ${sty['form__label-flex-mb']}`}>
                                <div className={sty['form__input-outer']}>
                                    <div className={`${sty['form__label']} ${sty['form__label-flex-mb']}`}>取得年</div>
                                    <Controller
                                        control={control}
                                        name={`qualifications.${index}.qualification_year`}
                                        rules={validationSchema.qualification_year(watch(qualification_name), watch(qualification_month))}
                                        render={({ field }) => (
                                            <select {...field}>
                                                <option hidden value="">選択</option>
                                                {years.map((y) => (
                                                    <option key={y} value={y}>{y + '年'}</option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                    {errors.qualifications?.[index]?.qualification_year && <p className="form__validation-msg" data-testid="pc_year_error_message">{errors.qualifications[index].qualification_year.message}</p>}

                                </div>
                                <div className={sty['form__input-outer']}>
                                    <div className={`${sty['form__label']} ${sty['form__label-flex-mb']}`}>取得月</div>
                                    <Controller
                                        control={control}
                                        name={`qualifications.${index}.qualification_month`}
                                        rules={validationSchema.qualification_month(watch(qualification_year), watch(qualification_month))}
                                        render={({ field }) => (
                                            <select {...field}>
                                                <option hidden value="">選択</option>
                                                {months.map((m) => (
                                                    <option key={m} value={m}>{m + '月'}</option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                    {errors.qualifications?.[index]?.qualification_month && <p className="form__validation-msg" data-testid="pc_month_error_message">{errors.qualifications[index].qualification_month.message}</p>}
                                </div>

                            </div>
                        </div>
                        {fields.length > 1 && (
                            <button type="button" className={sty['delete-each-btn']} onClick={() => remove(index)}>削除</button>
                        )}
                    </div>
                </div>
                )}
            )}

            <div className={sty['form__btn-left']}>
                <button type="button" className={`${sty['plus']} ${sty['plus-sp']}`} onClick={() => append({ qualification_name: '', qualification_year: '', qualification_month: '' })} data-testid="sp_plus_button">+</button>
            </div>
            <div className="primary-btns">
                <button type="submit" className="primary-btn">保存</button>
            </div>
        </form >
    )
}

export default Certifications;